import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { PeriodSelector } from '@/components/analytics'
import { LoadingScreen } from '@/components/LoadingScreen'
import { Card } from '@/components/ui'
import type { MetricWeek } from '@/hooks/useMetricWeeks'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { withAuthRecovery } from '@/lib/authRecovery'
import {
  getEffectiveWeeklyPlan,
  getPartnerMarketingTargetKey,
  type PartnerMarketingWeeklyPlanRow
} from '@/lib/partnerMarketingWeeklyPlan'
import { formatMarketingTargetologistName } from '@/lib/marketingTargetologistName'
import { formatCurrency } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { SURFACE_CARD_CLASS, SUBTLE_PANEL_CLASS, FILTER_CHIP_CLASS, INPUT_CLASS } from '@/components/marketing/marketingClasses'

const DEFAULT_ACCOUNT_CURRENCY = 'RUB'
const VAT_MULTIPLIER = 1.22
const MARKETING_VAT_STORAGE_KEY = 'partner-marketing-include-vat'

type FilterOption = { value: string; label: string; searchText?: string; disabled?: boolean }

interface ClickDailyRow {
  rowKey: string
  date: string
  service: string
  accountId: number
  accountName: string | null
  accountCurrency: string | null
  campaignId: number | null
  campaignName: string | null
  cost: number
  conversions: number
}

interface DailySummaryRow {
  key: string
  date: string
  accountLabel: string
  serviceLabel: string
  spend: number
  spendByCurrency: Record<string, number>
  conversions: number
  cpl: number | null
}

type DailySummarySort =
  | 'account_asc'
  | 'account_desc'
  | 'service_asc'
  | 'service_desc'
  | 'date_asc'
  | 'date_desc'
  | 'spend_asc'
  | 'spend_desc'
  | 'conversions_asc'
  | 'conversions_desc'
  | 'cpl_asc'
  | 'cpl_desc'

function normalizeCurrencyCode(value: string | null | undefined): string | null {
  if (!value) return null
  const normalized = value.trim().toUpperCase()
  return normalized || null
}

function getAccountCurrency(value: string | null | undefined): string {
  return normalizeCurrencyCode(value) || DEFAULT_ACCOUNT_CURRENCY
}

function getCurrencyCodes(spendByCurrency: Record<string, number>): string[] {
  return Object.keys(spendByCurrency).sort((a, b) => a.localeCompare(b, 'en'))
}

function getSingleCurrency(spendByCurrency: Record<string, number>): string | null {
  const currencies = getCurrencyCodes(spendByCurrency)
  return currencies.length === 1 ? currencies[0] : null
}

function getDisplayMoneyValue(value: number, includeVat: boolean): number {
  return includeVat ? value * VAT_MULTIPLIER : value
}

function formatNumber(value: number): string {
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })
}

function formatMoneyValue(value: number, currency: string | null, includeVat = false): string {
  const displayValue = getDisplayMoneyValue(value, includeVat)

  if (currency) {
    return formatCurrency(displayValue, currency)
  }

  return formatNumber(displayValue)
}

function formatMoneyByCurrencies(spendByCurrency: Record<string, number>, includeVat = false): string {
  const currencies = getCurrencyCodes(spendByCurrency)

  if (currencies.length === 0) {
    return formatMoneyValue(0, null, includeVat)
  }

  if (currencies.length === 1) {
    const currency = currencies[0]
    return formatMoneyValue(spendByCurrency[currency] || 0, currency, includeVat)
  }

  return currencies
    .map((currency) => formatMoneyValue(spendByCurrency[currency] || 0, currency, includeVat))
    .join(' • ')
}

function formatCpl(value: number | null, currency: string | null, includeVat = false): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return formatMoneyValue(value, currency, includeVat)
}

function formatFullDate(value: string): string {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

function formatInteger(value: number): string {
  return Math.round(value).toLocaleString('ru-RU')
}

function formatPercent(value: number): string {
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10
  return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('ru-RU', {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1
  })}%`
}

function formatPercentPlain(value: number): string {
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10
  return `${rounded.toLocaleString('ru-RU', {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1
  })}%`
}

function scaleSpendByCurrencies(spendByCurrency: Record<string, number>, ratio: number): Record<string, number> {
  return Object.fromEntries(
    Object.entries(spendByCurrency).map(([currency, amount]) => [currency, amount * ratio])
  )
}

function getInclusiveDays(start: string, end: string): number {
  const startDate = new Date(`${start}T00:00:00`)
  const endDate = new Date(`${end}T00:00:00`)
  return Math.max(1, Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1)
}

function getOverlapDays(rangeStart: string, rangeEnd: string, segmentStart: string, segmentEnd: string): number {
  const overlapStart = rangeStart > segmentStart ? rangeStart : segmentStart
  const overlapEnd = rangeEnd < segmentEnd ? rangeEnd : segmentEnd
  if (overlapStart > overlapEnd) return 0
  return getInclusiveDays(overlapStart, overlapEnd)
}

function getAccountDisplayLabel(row: ClickDailyRow): string {
  return formatMarketingTargetologistName(row.accountName) || `Таргетолог ${row.accountId}`
}

function getCampaignKey(row: ClickDailyRow): string {
  return row.campaignId !== null ? String(row.campaignId) : `${row.service}:${row.campaignName || 'Без названия'}`
}

function getCampaignDisplayLabel(row: ClickDailyRow): string {
  return row.campaignName || (row.campaignId !== null ? `Кампания ${row.campaignId}` : 'Без названия')
}

function formatSelectedItemsLabel(
  selectedValues: string[] | null,
  options: FilterOption[],
  allLabel: string,
  noun: string
): string {
  if (selectedValues === null || selectedValues.length >= options.length) return allLabel
  if (selectedValues.length === 0) return 'Ничего не выбрано'
  if (selectedValues.length === 1) {
    return options.find((option) => option.value === selectedValues[0])?.label || `1 ${noun}`
  }

  return `Выбрано ${noun}: ${selectedValues.length}`
}

function sortFilterOptions(options: FilterOption[]): FilterOption[] {
  return [...options].sort((a, b) => {
    if (Boolean(a.disabled) !== Boolean(b.disabled)) {
      return a.disabled ? 1 : -1
    }

    return a.label.localeCompare(b.label, 'ru')
  })
}

function MultiSelectFilter({
  label,
  selectedValues,
  onChange,
  options,
  allLabel,
  noun,
  searchPlaceholder
}: {
  label: string
  selectedValues: string[] | null
  onChange: (values: string[] | null) => void
  options: FilterOption[]
  allLabel: string
  noun: string
  searchPlaceholder: string
}) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const normalizedQuery = searchQuery.trim().toLowerCase()
  const enabledOptions = options.filter((option) => !option.disabled)
  const enabledValues = enabledOptions.map((option) => option.value)
  const allSelected = selectedValues === null || selectedValues.length >= enabledOptions.length
  const selectedValueSet = new Set(allSelected ? enabledValues : selectedValues)
  const filteredOptions = normalizedQuery
    ? options.filter((option) => `${option.label} ${option.searchText || ''}`.toLowerCase().includes(normalizedQuery))
    : options

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const normalizeSelection = (values: string[]) => {
    const nextValues = Array.from(new Set(values)).filter((value) => enabledOptions.some((option) => option.value === value))
    if (nextValues.length >= enabledOptions.length) {
      onChange(null)
      return
    }

    onChange(nextValues)
  }

  const toggleValue = (value: string) => {
    const option = options.find((item) => item.value === value)
    if (option?.disabled) return

    const currentValues = allSelected ? enabledValues : (selectedValues || [])

    if (currentValues.includes(value)) {
      normalizeSelection(currentValues.filter((item) => item !== value))
      return
    }

    normalizeSelection([...currentValues, value])
  }

  const selectedLabel = formatSelectedItemsLabel(selectedValues, options, allLabel, noun)

  return (
    <div ref={containerRef} className="relative space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex items-center justify-between text-left ${INPUT_CLASS}`}
      >
        <span className="truncate pr-3">{selectedLabel}</span>
        <span className={`text-xs text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
      </button>

      {open ? (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-border bg-card p-2 shadow-card">
          <div className="mb-2 flex items-center gap-2">
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className={`${INPUT_CLASS} py-2 text-xs`}
            />
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                onChange([])
              }}
              className="shrink-0 rounded-lg border border-border px-2.5 py-2 text-xs font-medium text-muted-foreground transition hover:bg-muted"
            >
              Сбросить
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            <label className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onChange(allSelected ? [] : null)}
                className="h-4 w-4 rounded border-border"
              />
              <span>{allLabel}</span>
            </label>

            <div className="my-1 border-t border-border/60" />

            {filteredOptions.map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                  option.disabled
                    ? 'cursor-not-allowed opacity-45'
                    : 'cursor-pointer hover:bg-muted'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedValueSet.has(option.value)}
                  disabled={option.disabled}
                  onChange={() => toggleValue(option.value)}
                  className="h-4 w-4 rounded border-border"
                />
                <span className="min-w-0 truncate">{option.label}</span>
              </label>
            ))}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Ничего не найдено.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function SortArrows({
  activeSort,
  ascValue,
  descValue,
  onChange
}: {
  activeSort: string
  ascValue: string
  descValue: string
  onChange: (value: DailySummarySort) => void
}) {
  const isAscActive = activeSort === ascValue
  const isDescActive = activeSort === descValue

  return (
    <span className="ml-1.5 inline-flex w-3.5 flex-col items-center justify-center leading-none align-middle">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onChange(ascValue as DailySummarySort)
        }}
        className={`h-2.5 text-[9px] leading-none transition ${isAscActive ? 'text-amber-700' : 'text-muted-foreground/50 hover:text-foreground'}`}
        aria-label="Сортировать по возрастанию"
      >
        ▲
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onChange(descValue as DailySummarySort)
        }}
        className={`h-2.5 text-[9px] leading-none transition ${isDescActive ? 'text-amber-700' : 'text-muted-foreground/50 hover:text-foreground'}`}
        aria-label="Сортировать по убыванию"
      >
        ▼
      </button>
    </span>
  )
}

function SortableHeader({
  label,
  activeSort,
  ascValue,
  descValue,
  onChange,
  align = 'left'
}: {
  label: string
  activeSort: DailySummarySort
  ascValue: DailySummarySort
  descValue: DailySummarySort
  onChange: (value: DailySummarySort) => void
  align?: 'left' | 'right'
}) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap ${align === 'right' ? 'justify-end' : ''}`}>
      <span>{label}</span>
      <SortArrows activeSort={activeSort} ascValue={ascValue} descValue={descValue} onChange={onChange} />
    </span>
  )
}

function mapClickDailyRows(data: any[] | null | undefined): ClickDailyRow[] {
  return ((data || []) as any[]).map((item, index) => ({
    rowKey: [
      item.metric_date,
      item.account_id,
      item.campaign_id ?? item.campaign_name ?? 'no-campaign',
      item.cost ?? 0,
      item.conversions ?? 0,
      index
    ].join(':'),
    date: item.metric_date,
    service: item.service || 'Без сервиса',
    accountId: Number(item.account_id),
    accountName: item.account_name || null,
    accountCurrency: getAccountCurrency(item.account_currency),
    campaignId: item.campaign_id === null ? null : Number(item.campaign_id),
    campaignName: item.campaign_name || null,
    cost: Number(item.cost || 0),
    conversions: Number(item.conversions || 0)
  })) as ClickDailyRow[]
}

function buildDailySummaryRows(rows: ClickDailyRow[]): DailySummaryRow[] {
  const map = new Map<string, DailySummaryRow>()

  for (const row of rows) {
    const key = `${row.date}:${row.service}:${row.accountId}`
    const current = map.get(key) || {
      key,
      date: row.date,
      accountLabel: getAccountDisplayLabel(row),
      serviceLabel: row.service,
      spend: 0,
      spendByCurrency: {},
      conversions: 0,
      cpl: null
    }

    current.spend += row.cost
    const currencyKey = getAccountCurrency(row.accountCurrency)
    current.spendByCurrency[currencyKey] = (current.spendByCurrency[currencyKey] || 0) + row.cost
    current.conversions += row.conversions
    current.cpl = current.conversions > 0 ? current.spend / current.conversions : null
    map.set(key, current)
  }

  return Array.from(map.values())
    .sort((a, b) => (
      b.date.localeCompare(a.date)
      || a.accountLabel.localeCompare(b.accountLabel, 'ru')
      || a.serviceLabel.localeCompare(b.serviceLabel, 'ru')
    ))
}

function KpiCard({
  label,
  value,
  secondaryValue,
  cornerValue,
  cornerTone = 'default',
  progressValue,
  progressTone = 'default',
  progressMarkerValue,
  planToDateLabel,
  planToDateValue,
  footerTiles,
  reserveProgressSpace = false
}: {
  label: string
  value: string
  secondaryValue?: string
  cornerValue?: string | null
  cornerTone?: 'default' | 'positive' | 'negative' | 'warning'
  progressValue?: string | null
  progressTone?: 'default' | 'positive' | 'negative' | 'warning'
  progressMarkerValue?: string | null
  planToDateLabel?: string
  planToDateValue?: string
  reserveProgressSpace?: boolean
  footerTiles?: Array<{
    label: string
    value: string
    hint?: string
    tone?: 'default' | 'positive' | 'negative' | 'warning'
  }>
}) {
  const getToneClasses = (tone: 'default' | 'positive' | 'negative' | 'warning' = 'default') => {
    switch (tone) {
      case 'positive':
        return { text: 'text-emerald-700', panel: 'border-emerald-200 bg-emerald-50', bar: 'bg-emerald-500' }
      case 'negative':
        return { text: 'text-red-600', panel: 'border-red-200 bg-red-50', bar: 'bg-red-500' }
      case 'warning':
        return { text: 'text-amber-700', panel: 'border-amber-200 bg-amber-50', bar: 'bg-amber-500' }
      default:
        return { text: 'text-muted-foreground', panel: 'border-border bg-muted', bar: 'bg-amber-500' }
    }
  }

  const normalizedProgress = progressValue ? Number.parseFloat(progressValue) : null
  const progressWidth = normalizedProgress === null || Number.isNaN(normalizedProgress)
    ? 0
    : Math.max(0, Math.min(100, normalizedProgress))
  const normalizedProgressMarker = progressMarkerValue ? Number.parseFloat(progressMarkerValue) : null
  const progressMarkerWidth = normalizedProgressMarker === null || Number.isNaN(normalizedProgressMarker)
    ? progressWidth
    : Math.max(0, Math.min(100, normalizedProgressMarker))
  const cornerStyle = getToneClasses(cornerTone)
  const progressStyle = getToneClasses(progressTone)
  const hasProgressSection = Boolean(progressValue) || reserveProgressSpace

  return (
    <Card className={`${SURFACE_CARD_CLASS} h-full overflow-hidden`}>
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="h-1 w-14 rounded-full bg-amber-500" />
        <div className="flex items-start justify-between gap-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
          {cornerValue ? (
            <div className={`rounded-full border px-3 py-1 text-lg font-semibold leading-none ${cornerStyle.panel} ${cornerStyle.text}`}>
              {cornerValue}
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[40px] font-semibold leading-none tracking-tight text-foreground">{value}</span>
          {secondaryValue ? <span className="text-2xl font-medium text-muted-foreground/70">/ {secondaryValue}</span> : null}
        </div>

        {progressValue ? (
          <div className="space-y-3 border-t border-border/60 pt-3">
            <div className="relative h-5">
              <div className="absolute inset-x-0 top-1 h-3 overflow-hidden rounded-full bg-muted ring-1 ring-border">
                <div className={`h-full rounded-full transition-all ${progressStyle.bar}`} style={{ width: `${progressWidth}%` }} />
              </div>
              <div
                className="absolute top-0 z-10 h-5 w-[2px] rounded-full bg-foreground shadow-sm"
                style={{ left: `${progressMarkerWidth}%`, transform: 'translateX(-50%)' }}
              />
            </div>
            {planToDateLabel && planToDateValue ? (
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{planToDateLabel}</span>
                <span className="font-semibold text-foreground">{planToDateValue}</span>
              </div>
            ) : null}
          </div>
        ) : reserveProgressSpace ? (
          <div className="border-t border-border/60 pt-3">
            <div className="h-[52px]" />
          </div>
        ) : null}

        {footerTiles && footerTiles.length > 0 ? (
          <div className={`${hasProgressSection ? 'mt-auto ' : ''}grid gap-3 ${footerTiles.length > 1 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
            {footerTiles.map((tile) => {
              const tileStyle = getToneClasses(tile.tone)
              return (
                <div key={tile.label} className={`rounded-2xl border px-3.5 py-3 ${tileStyle.panel}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{tile.label}</div>
                  <div className={`mt-1 text-xl font-semibold ${tileStyle.text}`}>{tile.value}</div>
                  {tile.hint ? <div className="mt-1 text-xs text-muted-foreground">{tile.hint}</div> : null}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
    </Card>
  )
}

export function PartnerMarketingDailySummaryPage() {
  const { partnerNumber, studioId } = useParams<{ partnerNumber: string; studioId?: string }>()
  const [searchParams] = useSearchParams()
  const initialServiceParamsRef = useRef(searchParams.getAll('service').filter(Boolean))
  const initialAccountParamsRef = useRef(searchParams.getAll('accountId').filter(Boolean))
  const didApplySearchFiltersRef = useRef(false)
  const parsedPartnerNumber = Number(partnerNumber)
  const parsedStudioId = studioId ? Number(studioId) : undefined

  const periods = useStudioPeriods()
  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange
  } = useAnalyticsTimeFilter(periods.currentPeriod)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<ClickDailyRow[]>([])
  const [planWeeks, setPlanWeeks] = useState<MetricWeek[]>([])
  const [planRowsData, setPlanRowsData] = useState<PartnerMarketingWeeklyPlanRow[]>([])
  const [selectedServices, setSelectedServices] = useState<string[] | null>(null)
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[] | null>(null)
  const [selectedCampaignKeys, setSelectedCampaignKeys] = useState<string[] | null>(null)
  const [sort, setSort] = useState<DailySummarySort>('date_desc')
  const [includeVat, setIncludeVat] = useState(() => {
    if (typeof window === 'undefined') return false

    try {
      return localStorage.getItem(MARKETING_VAT_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  useEffect(() => {
    const periodFilter = searchParams.get('periodFilter')
    if (!periodFilter) return
    if (!timeFilterOptions.some((option) => option.value === periodFilter)) return
    if (selectedTimeFilter === periodFilter) return
    setSelectedTimeFilter(periodFilter)
  }, [searchParams, selectedTimeFilter, setSelectedTimeFilter, timeFilterOptions])

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      localStorage.setItem(MARKETING_VAT_STORAGE_KEY, includeVat ? '1' : '0')
    } catch {
      // Ignore storage errors and keep the current in-memory toggle state.
    }
  }, [includeVat])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber)) {
      setLoading(false)
      setError('Не удалось определить франчайзи')
      setRows([])
      return
    }

    if (periods.loading) return

    if (!periods.currentPeriod || !selectedRange) {
      setLoading(false)
      setRows([])
      setError(periods.error ? 'Не удалось загрузить период аналитики' : null)
      return
    }

    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        const result = await withAuthRecovery(() =>
          (supabase as any)
            .rpc('get_partner_click_stats', {
              p_partner_number: parsedPartnerNumber,
              p_date_from: selectedRange.start,
              p_date_to: selectedRange.end
            })
        )

        if (result.error) throw result.error
        setRows(mapClickDailyRows(result.data))
      } catch (loadError) {
        console.error('❌ PartnerMarketingDailySummaryPage load failed:', loadError)
        setError('Не удалось загрузить маркетинговые данные')
        setRows([])
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [parsedPartnerNumber, periods.currentPeriod, periods.error, periods.loading, selectedRange])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber) || !selectedRange || !periods.currentPeriod) {
      setPlanWeeks([])
      setPlanRowsData([])
      return
    }

    let cancelled = false

    const loadPlanContext = async () => {
      try {
        const { data: rawWeeks, error: weeksError } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('*')
            .eq('period_id', periods.currentPeriod!.period_id)
            .order('week_number_in_period', { ascending: true })
        )

        if (weeksError) throw weeksError

        const overlappingWeeks = ((rawWeeks as MetricWeek[] | null) || [])
        if (!cancelled) setPlanWeeks(overlappingWeeks)

        const weekIds = overlappingWeeks.map((week) => week.week_id).filter((weekId) => weekId > 0)
        if (weekIds.length === 0) {
          if (!cancelled) setPlanRowsData([])
          return
        }

        const { data: rawPlans, error: plansError } = await withAuthRecovery(() =>
          (supabase as any)
            .from('click_weekly_plan')
            .select('partner_number, week_id, service, account_id, planning_status, leads_plan, spend_plan, updated_at, updated_by_user_id')
            .eq('partner_number', parsedPartnerNumber)
            .in('week_id', weekIds)
        )

        if (plansError) throw plansError
        if (!cancelled) setPlanRowsData(((rawPlans || []) as PartnerMarketingWeeklyPlanRow[]))
      } catch (plansError) {
        console.error('❌ PartnerMarketingDailySummaryPage plan context load failed:', plansError)
        if (!cancelled) {
          setPlanWeeks([])
          setPlanRowsData([])
        }
      }
    }

    loadPlanContext()

    return () => {
      cancelled = true
    }
  }, [parsedPartnerNumber, periods.currentPeriod, selectedRange])

  const rowsForServiceOptions = useMemo(() => {
    return rows.filter((row) => {
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      if (selectedCampaignKeys !== null && selectedCampaignKeys.length > 0 && !selectedCampaignKeys.includes(getCampaignKey(row))) return false
      return true
    })
  }, [rows, selectedAccountIds, selectedCampaignKeys])

  const rowsForAccountOptions = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedCampaignKeys !== null && selectedCampaignKeys.length > 0 && !selectedCampaignKeys.includes(getCampaignKey(row))) return false
      return true
    })
  }, [rows, selectedServices, selectedCampaignKeys])

  const rowsForCampaignOptions = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      return true
    })
  }, [rows, selectedServices, selectedAccountIds])

  const serviceOptions = useMemo(() => {
    const allValues = Array.from(new Set(rows.map((row) => row.service))).sort((a, b) => a.localeCompare(b, 'ru'))
    const enabledValues = new Set(rowsForServiceOptions.map((row) => row.service))
    return sortFilterOptions(
      allValues.map((value) => ({ value, label: value, disabled: !enabledValues.has(value) }))
    )
  }, [rows, rowsForServiceOptions])

  const accountOptions = useMemo(() => {
    const allValues = Array.from(
      new Map(
        rows.map((row) => [
          String(row.accountId),
          {
            label: getAccountDisplayLabel(row),
            searchText: `${getAccountDisplayLabel(row)} ${row.accountName || ''} ${row.accountId}`
          }
        ])
      ).entries()
    )
    const enabledValues = new Set(rowsForAccountOptions.map((row) => String(row.accountId)))

    return sortFilterOptions(
      allValues.map(([value, item]) => ({ value, label: item.label, searchText: item.searchText, disabled: !enabledValues.has(value) }))
    )
  }, [rows, rowsForAccountOptions])

  const campaignOptions = useMemo(() => {
    const allValues = Array.from(
      new Map(
        rows.map((row) => [
          getCampaignKey(row),
          {
            value: getCampaignKey(row),
            label: getCampaignDisplayLabel(row),
            searchText: `${getCampaignDisplayLabel(row)} ${row.campaignId ?? ''} ${getAccountDisplayLabel(row)} ${row.service}`
          }
        ])
      ).values()
    )
    const enabledValues = new Set(rowsForCampaignOptions.map((row) => getCampaignKey(row)))

    return sortFilterOptions(
      allValues.map((option) => ({
        ...option,
        disabled: !enabledValues.has(option.value)
      }))
    )
  }, [rows, rowsForCampaignOptions])

  useEffect(() => {
    if (didApplySearchFiltersRef.current) return
    if (rows.length === 0) return

    const nextServices = initialServiceParamsRef.current.filter((value) => serviceOptions.some((option) => option.value === value))
    const nextAccounts = initialAccountParamsRef.current.filter((value) => accountOptions.some((option) => option.value === value))

    setSelectedServices(nextServices.length > 0 ? nextServices : null)
    setSelectedAccountIds(nextAccounts.length > 0 ? nextAccounts : null)
    didApplySearchFiltersRef.current = true
  }, [accountOptions, rows.length, serviceOptions])

  useEffect(() => {
    setSelectedServices((current) => current === null ? null : current.filter((value) => serviceOptions.some((option) => option.value === value && !option.disabled)))
  }, [serviceOptions])

  useEffect(() => {
    setSelectedAccountIds((current) => current === null ? null : current.filter((value) => accountOptions.some((option) => option.value === value && !option.disabled)))
  }, [accountOptions])

  useEffect(() => {
    setSelectedCampaignKeys((current) => current === null ? null : current.filter((value) => campaignOptions.some((option) => option.value === value && !option.disabled)))
  }, [campaignOptions])

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      if (selectedCampaignKeys !== null && selectedCampaignKeys.length > 0 && !selectedCampaignKeys.includes(getCampaignKey(row))) return false
      return true
    })
  }, [rows, selectedServices, selectedAccountIds, selectedCampaignKeys])

  const rowsForPlan = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      return true
    })
  }, [rows, selectedServices, selectedAccountIds])

  const planHint = selectedCampaignKeys !== null && selectedCampaignKeys.length > 0
    ? 'План в карточках не учитывает фильтр по кампаниям и считается по таргетологу целиком.'
    : null

  const planWeeksInScope = useMemo(() => {
    if (!selectedRange) return [] as MetricWeek[]
    if (selectedRange.mode === 'week' && selectedRange.weekNumber) {
      return planWeeks.filter((week) => week.week_number_in_period === selectedRange.weekNumber)
    }
    return planWeeks
  }, [planWeeks, selectedRange])

  const planDraftsByPeriod = useMemo(() => {
    const result = new Map<number, Record<string, Record<number, { status?: 'planned' | 'unset' | 'excluded'; leads?: number | null; spend?: number | null }>>>()
    const weeksByPeriod = new Map<number, MetricWeek[]>()

    planWeeks.forEach((week) => {
      const current = weeksByPeriod.get(week.period_id) || []
      current.push(week)
      weeksByPeriod.set(week.period_id, current)
    })

    weeksByPeriod.forEach((periodWeeks, periodId) => {
      const weekNumberById = new Map(periodWeeks.map((week) => [week.week_id, week.week_number_in_period]))
      const draft: Record<string, Record<number, { status?: 'planned' | 'unset' | 'excluded'; leads?: number | null; spend?: number | null }>> = {}

      planRowsData
        .filter((row) => weekNumberById.has(row.week_id))
        .forEach((row) => {
          const weekNumber = weekNumberById.get(row.week_id)
          if (!weekNumber) return

          const targetKey = getPartnerMarketingTargetKey(row.service, row.account_id)
          const targetDraft = draft[targetKey] || {}
          targetDraft[weekNumber] = {
            status: row.planning_status,
            leads: row.leads_plan,
            spend: row.spend_plan
          }
          draft[targetKey] = targetDraft
        })

      result.set(periodId, draft)
    })

    return result
  }, [planRowsData, planWeeks])

  const planByTarget = useMemo(() => {
    if (!selectedRange) return new Map<string, { leads: number; spend: number; currency: string | null }>()

    const result = new Map<string, { leads: number; spend: number; currency: string | null }>()
    const targetMeta = new Map<string, string | null>()
    const weeksByPeriod = new Map<number, MetricWeek[]>()

    planWeeksInScope.forEach((week) => {
      const current = weeksByPeriod.get(week.period_id) || []
      current.push(week)
      weeksByPeriod.set(week.period_id, current)
    })

    rowsForPlan.forEach((row) => {
      const targetKey = getPartnerMarketingTargetKey(row.service, row.accountId)
      if (!targetMeta.has(targetKey)) targetMeta.set(targetKey, row.accountCurrency || null)
    })

    targetMeta.forEach((currency, targetKey) => {
      const aggregate = { leads: 0, spend: 0, currency }

      planWeeksInScope.forEach((week) => {
        const periodWeeks = weeksByPeriod.get(week.period_id) || []
        const periodDraft = planDraftsByPeriod.get(week.period_id) || {}
        const plan = getEffectiveWeeklyPlan(targetKey, week.week_number_in_period, periodDraft, periodWeeks)
        if (plan.status !== 'planned') return

        const overlapDays = getOverlapDays(selectedRange.start, selectedRange.end, week.week_start, week.week_end)
        if (overlapDays <= 0) return

        const weekBaseDays = Math.max(1, week.week_len_days || getInclusiveDays(week.week_start, week.week_end))
        const ratio = overlapDays / weekBaseDays
        aggregate.leads += (plan.leads || 0) * ratio
        aggregate.spend += (plan.spend || 0) * ratio
      })

      result.set(targetKey, aggregate)
    })

    return result
  }, [planDraftsByPeriod, planWeeksInScope, rowsForPlan, selectedRange])

  const summaryRows = useMemo(() => {
    const items = buildDailySummaryRows(filteredRows)

    items.sort((a, b) => {
      switch (sort) {
        case 'account_asc':
          return a.accountLabel.localeCompare(b.accountLabel, 'ru') || b.date.localeCompare(a.date) || a.serviceLabel.localeCompare(b.serviceLabel, 'ru')
        case 'account_desc':
          return b.accountLabel.localeCompare(a.accountLabel, 'ru') || b.date.localeCompare(a.date) || a.serviceLabel.localeCompare(b.serviceLabel, 'ru')
        case 'service_asc':
          return a.serviceLabel.localeCompare(b.serviceLabel, 'ru') || a.accountLabel.localeCompare(b.accountLabel, 'ru') || b.date.localeCompare(a.date)
        case 'service_desc':
          return b.serviceLabel.localeCompare(a.serviceLabel, 'ru') || a.accountLabel.localeCompare(b.accountLabel, 'ru') || b.date.localeCompare(a.date)
        case 'date_asc':
          return a.date.localeCompare(b.date) || a.accountLabel.localeCompare(b.accountLabel, 'ru') || a.serviceLabel.localeCompare(b.serviceLabel, 'ru')
        case 'date_desc':
          return b.date.localeCompare(a.date) || a.accountLabel.localeCompare(b.accountLabel, 'ru') || a.serviceLabel.localeCompare(b.serviceLabel, 'ru')
        case 'spend_asc':
          return a.spend - b.spend || b.date.localeCompare(a.date) || a.accountLabel.localeCompare(b.accountLabel, 'ru')
        case 'spend_desc':
          return b.spend - a.spend || b.date.localeCompare(a.date) || a.accountLabel.localeCompare(b.accountLabel, 'ru')
        case 'conversions_asc':
          return a.conversions - b.conversions || b.spend - a.spend || b.date.localeCompare(a.date)
        case 'conversions_desc':
          return b.conversions - a.conversions || b.spend - a.spend || b.date.localeCompare(a.date)
        case 'cpl_asc': {
          const aValid = a.cpl !== null && Number.isFinite(a.cpl)
          const bValid = b.cpl !== null && Number.isFinite(b.cpl)
          if (aValid !== bValid) return aValid ? -1 : 1
          if (!aValid && !bValid) return b.spend - a.spend || b.date.localeCompare(a.date)
          return (a.cpl || 0) - (b.cpl || 0) || b.conversions - a.conversions || b.date.localeCompare(a.date)
        }
        case 'cpl_desc': {
          const aValid = a.cpl !== null && Number.isFinite(a.cpl)
          const bValid = b.cpl !== null && Number.isFinite(b.cpl)
          if (aValid !== bValid) return aValid ? -1 : 1
          if (!aValid && !bValid) return b.spend - a.spend || b.date.localeCompare(a.date)
          return (b.cpl || 0) - (a.cpl || 0) || b.conversions - a.conversions || b.date.localeCompare(a.date)
        }
        default:
          return 0
      }
    })

    return items
  }, [filteredRows, sort])

  const totals = useMemo(() => {
    return filteredRows.reduce((acc, row) => {
      acc.spend += row.cost
      acc.conversions += row.conversions
      const currencyKey = getAccountCurrency(row.accountCurrency)
      acc.spendByCurrency[currencyKey] = (acc.spendByCurrency[currencyKey] || 0) + row.cost
      return acc
    }, {
      spend: 0,
      spendByCurrency: {} as Record<string, number>,
      conversions: 0
    })
  }, [filteredRows])

  const selectedCurrency = getSingleCurrency(totals.spendByCurrency)
  const hasMixedCurrencies = getCurrencyCodes(totals.spendByCurrency).length > 1
  const averageCpa = selectedCurrency && totals.conversions > 0 ? totals.spend / totals.conversions : null

  const planTotals = useMemo(() => {
    let spend = 0
    let leads = 0
    const spendByCurrency: Record<string, number> = {}

    planByTarget.forEach((item) => {
      spend += item.spend
      leads += item.leads
      const currencyKey = getAccountCurrency(item.currency)
      spendByCurrency[currencyKey] = (spendByCurrency[currencyKey] || 0) + item.spend
    })

    return {
      spend,
      leads,
      spendByCurrency,
      cpa: leads > 0 ? spend / leads : null
    }
  }, [planByTarget])

  const rangeProgress = useMemo(() => {
    if (!selectedRange) {
      return { elapsedDays: 0, totalDays: 0, hasStarted: false, isCompleted: false }
    }

    const rangeStart = new Date(`${selectedRange.start}T00:00:00`)
    const rangeEnd = new Date(`${selectedRange.end}T00:00:00`)
    const yesterday = new Date()
    yesterday.setHours(0, 0, 0, 0)
    yesterday.setDate(yesterday.getDate() - 1)

    const effectiveEnd = rangeEnd < yesterday ? rangeEnd : yesterday
    if (effectiveEnd < rangeStart) {
      return {
        elapsedDays: 0,
        totalDays: Math.max(1, selectedRange.rangeDays || 1),
        hasStarted: false,
        isCompleted: false
      }
    }

    return {
      elapsedDays: Math.floor((effectiveEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1,
      totalDays: Math.max(1, selectedRange.rangeDays || 1),
      hasStarted: true,
      isCompleted: rangeEnd <= yesterday
    }
  }, [selectedRange])

  const spendPlanTodayByCurrency = useMemo(() => {
    if (rangeProgress.totalDays <= 0 || rangeProgress.elapsedDays <= 0) return {} as Record<string, number>
    return scaleSpendByCurrencies(planTotals.spendByCurrency, rangeProgress.elapsedDays / rangeProgress.totalDays)
  }, [planTotals.spendByCurrency, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const spendForecastByCurrency = useMemo(() => {
    if (rangeProgress.elapsedDays <= 0 || rangeProgress.totalDays <= 0) return {} as Record<string, number>
    return scaleSpendByCurrencies(totals.spendByCurrency, rangeProgress.totalDays / rangeProgress.elapsedDays)
  }, [totals.spendByCurrency, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const leadsPlanToday = useMemo(() => {
    if (rangeProgress.totalDays <= 0 || rangeProgress.elapsedDays <= 0) return 0
    return (planTotals.leads * rangeProgress.elapsedDays) / rangeProgress.totalDays
  }, [planTotals.leads, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const leadsForecast = useMemo(() => {
    if (rangeProgress.totalDays <= 0 || rangeProgress.elapsedDays <= 0) return null
    return (totals.conversions * rangeProgress.totalDays) / rangeProgress.elapsedDays
  }, [totals.conversions, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const spendProgress = useMemo(() => {
    if (!rangeProgress.hasStarted || hasMixedCurrencies || planTotals.spend <= 0) return null
    return (totals.spend / planTotals.spend) * 100
  }, [hasMixedCurrencies, planTotals.spend, rangeProgress.hasStarted, totals.spend])

  const spendPlanToDatePercent = useMemo(() => {
    if (!rangeProgress.hasStarted || planTotals.spend <= 0) return null
    return ((Object.values(spendPlanTodayByCurrency)[0] || 0) / planTotals.spend) * 100
  }, [planTotals.spend, rangeProgress.hasStarted, spendPlanTodayByCurrency])

  const leadsProgress = useMemo(() => {
    if (!rangeProgress.hasStarted || planTotals.leads <= 0) return null
    return (totals.conversions / planTotals.leads) * 100
  }, [planTotals.leads, rangeProgress.hasStarted, totals.conversions])

  const leadsPlanToDatePercent = useMemo(() => {
    if (!rangeProgress.hasStarted || planTotals.leads <= 0) return null
    return (leadsPlanToday / planTotals.leads) * 100
  }, [leadsPlanToday, planTotals.leads, rangeProgress.hasStarted])

  const spendCornerTone = useMemo<'default' | 'positive' | 'negative'>(() => {
    if (spendProgress === null || spendPlanToDatePercent === null) return 'default'
    return Math.abs(spendProgress - spendPlanToDatePercent) <= 2 ? 'positive' : 'negative'
  }, [spendPlanToDatePercent, spendProgress])

  const leadsCornerTone = useMemo<'default' | 'positive' | 'negative'>(() => {
    if (leadsProgress === null || leadsPlanToDatePercent === null) return 'default'
    return leadsProgress >= leadsPlanToDatePercent ? 'positive' : 'negative'
  }, [leadsPlanToDatePercent, leadsProgress])

  const spendForecastTile = useMemo(() => {
    const forecastLabel = rangeProgress.isCompleted ? 'Факт периода' : 'Прогноз на период'
    if (!rangeProgress.hasStarted) return { label: forecastLabel, value: 'Диапазон не начался', tone: 'default' as const }
    if (Object.keys(spendForecastByCurrency).length === 0) return { label: forecastLabel, value: 'Недостаточно факта', tone: 'default' as const }
    return {
      label: forecastLabel,
      value: `${formatMoneyByCurrencies(spendForecastByCurrency, includeVat)} из ${formatMoneyByCurrencies(planTotals.spendByCurrency, includeVat)}`,
      tone: 'warning' as const
    }
  }, [includeVat, planTotals.spendByCurrency, rangeProgress.hasStarted, rangeProgress.isCompleted, spendForecastByCurrency])

  const spendDeltaTile = useMemo(() => {
    if (!rangeProgress.hasStarted) return { label: 'Отклонение', value: 'Диапазон не начался', tone: 'default' as const }
    if (hasMixedCurrencies) return { label: 'Отклонение', value: 'Уточните валюту', tone: 'warning' as const }
    if (planTotals.spend <= 0) return { label: 'Отклонение', value: 'План не задан', tone: 'default' as const }

    const forecastSpend = Object.values(spendForecastByCurrency)[0] || 0
    const diff = forecastSpend - planTotals.spend
    if (Math.abs(diff) < 0.01) return { label: 'В плане', value: formatMoneyValue(0, selectedCurrency, includeVat), tone: 'default' as const }

    return {
      label: rangeProgress.isCompleted ? (diff > 0 ? 'Выше плана на' : 'Ниже плана на') : (diff > 0 ? 'Риск перерасхода' : 'Риск недобора'),
      value: formatMoneyValue(Math.abs(diff), selectedCurrency, includeVat),
      tone: 'negative' as const
    }
  }, [hasMixedCurrencies, includeVat, planTotals.spend, rangeProgress.hasStarted, rangeProgress.isCompleted, selectedCurrency, spendForecastByCurrency])

  const leadsForecastTile = useMemo(() => {
    const forecastLabel = rangeProgress.isCompleted ? 'Факт периода' : 'Прогноз на период'
    if (!rangeProgress.hasStarted) return { label: forecastLabel, value: 'Диапазон не начался', tone: 'default' as const }
    if (leadsForecast === null) return { label: forecastLabel, value: 'Недостаточно факта', tone: 'default' as const }
    return {
      label: forecastLabel,
      value: `${formatInteger(leadsForecast)} из ${formatInteger(planTotals.leads)}`,
      tone: 'warning' as const
    }
  }, [leadsForecast, planTotals.leads, rangeProgress.hasStarted, rangeProgress.isCompleted])

  const leadsDeltaTile = useMemo(() => {
    if (!rangeProgress.hasStarted) return { label: 'Риск недобора', value: 'Диапазон не начался', tone: 'default' as const }
    if (planTotals.leads <= 0) return { label: 'Риск недобора', value: 'План не задан', tone: 'default' as const }
    if (leadsForecast === null) return { label: 'Риск недобора', value: 'Недостаточно факта', tone: 'default' as const }

    const gap = leadsForecast - planTotals.leads
    if (Math.abs(gap) < 0.01) return { label: 'В плане', value: formatNumber(0), tone: 'default' as const }
    return {
      label: gap < 0 ? 'Риск недобора' : 'Выше плана на',
      value: formatInteger(Math.abs(gap)),
      tone: gap < 0 ? ('negative' as const) : ('positive' as const)
    }
  }, [leadsForecast, planTotals.leads, rangeProgress.hasStarted])

  const cplDeviationState = useMemo(() => {
    if (hasMixedCurrencies) return { value: 'Уточните валюту', hint: 'Для CPL лучше смотреть выборку в одной валюте.', tone: 'warning' as const }
    if (planTotals.cpa === null || planTotals.cpa <= 0) return { value: 'План не задан', hint: undefined, tone: 'default' as const }
    if (averageCpa === null) return { value: 'Нет лидов для расчета', hint: undefined, tone: 'default' as const }

    const deviationPct = ((averageCpa - planTotals.cpa) / planTotals.cpa) * 100
    if (Math.abs(deviationPct) < 0.1) return { value: 'На плане', hint: undefined, tone: 'default' as const }
    return {
      value: `${formatPercent(deviationPct)} к плану`,
      hint: undefined,
      tone: deviationPct > 0 ? ('negative' as const) : ('positive' as const)
    }
  }, [averageCpa, hasMixedCurrencies, planTotals.cpa])

  const cplDeltaTile = useMemo(() => {
    if (hasMixedCurrencies) return { label: 'Сравнение с планом', value: 'Уточните валюту', tone: 'warning' as const }
    if (averageCpa === null || planTotals.cpa === null || planTotals.cpa <= 0) return { label: 'Сравнение с планом', value: 'План не задан', tone: 'default' as const }
    const diff = averageCpa - planTotals.cpa
    if (Math.abs(diff) < 0.01) return { label: 'Сравнение с планом', value: formatMoneyValue(0, selectedCurrency, includeVat), tone: 'default' as const }
    return {
      label: diff > 0 ? 'Выше плана на' : 'Ниже плана на',
      value: formatMoneyValue(Math.abs(diff), selectedCurrency, includeVat),
      tone: diff > 0 ? ('negative' as const) : ('positive' as const)
    }
  }, [averageCpa, hasMixedCurrencies, includeVat, planTotals.cpa, selectedCurrency])

  const spendPlanLabel = rangeProgress.isCompleted
    ? `План периода (${formatPercentPlain(100)})`
    : spendPlanToDatePercent !== null
      ? `План на вчера (${formatPercentPlain(spendPlanToDatePercent)})`
      : 'План на вчера'

  const leadsPlanLabel = rangeProgress.isCompleted
    ? `План периода (${formatPercentPlain(100)})`
    : leadsPlanToDatePercent !== null
      ? `План на вчера (${formatPercentPlain(leadsPlanToDatePercent)})`
      : 'План на вчера'

  const shouldReserveCplProgressSpace = spendProgress !== null || leadsProgress !== null

  const selectedServiceLabel = formatSelectedItemsLabel(selectedServices, serviceOptions, 'Все сервисы', 'сервисов')
  const selectedAccountsLabel = formatSelectedItemsLabel(selectedAccountIds, accountOptions, 'Все таргетологи', 'таргетологов')
  const selectedCampaignsLabel = formatSelectedItemsLabel(selectedCampaignKeys, campaignOptions, 'Все кампании', 'кампаний')

  if (periods.loading || loading) {
    return <LoadingScreen />
  }

  if (parsedStudioId && Number.isFinite(parsedStudioId)) {
    return <Navigate to={`/partners/${parsedPartnerNumber}/marketing/daily`} replace />
  }

  return (
    <div className="space-y-4">
      <Card className={`${SURFACE_CARD_CLASS} overflow-hidden p-0`}>
        <div className="space-y-3 px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <Link
                to={`/partners/${parsedPartnerNumber}/marketing`}
                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Назад в обзор
              </Link>
              <div>
                <h1 className="text-lg font-semibold">Сводка по таргетологам</h1>
                <p className="text-sm text-muted-foreground">
                  Дневная таблица по таргетологам, каналам и кампаниям из рекламных кабинетов Click.
                </p>
              </div>
            </div>

            <Link
              to={`/partners/${parsedPartnerNumber}/marketing/plan`}
              className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-4 py-2 text-sm font-medium text-amber-800 transition hover:bg-amber-200"
            >
              План по таргетологам
            </Link>
          </div>

          <div className={`${SUBTLE_PANEL_CLASS} px-3 py-2 sm:px-4 sm:py-2.5`}>
            <PeriodSelector
              currentPeriod={periods.currentPeriod}
              onPrevPeriod={periods.goToPrevPeriod}
              onNextPeriod={periods.goToNextPeriod}
              canGoPrev={periods.canGoPrev}
              canGoNext={periods.canGoNext}
              showViewModeSelector
              isCurrentPeriod={periods.isCurrentPeriod}
              periodFilterValue={selectedTimeFilter}
              periodFilterOptions={timeFilterOptions}
              onPeriodFilterChange={setSelectedTimeFilter}
              selectedRange={selectedRange}
              compact
            />
          </div>
        </div>
      </Card>

      <Card className={SURFACE_CARD_CLASS}>
        <div className="space-y-3 p-4">
          <div className="grid gap-4 md:grid-cols-3">
            <MultiSelectFilter
              label="Сервис"
              selectedValues={selectedServices}
              onChange={setSelectedServices}
              options={serviceOptions}
              allLabel="Все сервисы"
              noun="сервисов"
              searchPlaceholder="Найти сервис"
            />
            <MultiSelectFilter
              label="Таргетолог"
              selectedValues={selectedAccountIds}
              onChange={setSelectedAccountIds}
              options={accountOptions}
              allLabel="Все таргетологи"
              noun="таргетологов"
              searchPlaceholder="Найти таргетолога"
            />
            <MultiSelectFilter
              label="Кампания"
              selectedValues={selectedCampaignKeys}
              onChange={setSelectedCampaignKeys}
              options={campaignOptions}
              allLabel="Все кампании"
              noun="кампаний"
              searchPlaceholder="Найти кампанию"
            />
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <span className={FILTER_CHIP_CLASS}>{selectedServiceLabel}</span>
              <span className={FILTER_CHIP_CLASS}>{selectedAccountsLabel}</span>
              <span className={FILTER_CHIP_CLASS}>{selectedCampaignsLabel}</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedServices(null)
                  setSelectedAccountIds(null)
                  setSelectedCampaignKeys(null)
                }}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted"
              >
                Сбросить фильтры
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">Денежные показатели</span>
              <div className="inline-flex rounded-full border border-border bg-muted p-1">
                <button
                  type="button"
                  onClick={() => setIncludeVat(false)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    !includeVat
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Без НДС
                </button>
                <button
                  type="button"
                  onClick={() => setIncludeVat(true)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                    includeVat
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  С НДС +22%
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border border-red-300">
          <p className="text-sm text-red-600">{error}</p>
        </Card>
      )}

      <div className="space-y-2">
        {planHint ? <p className="text-xs text-muted-foreground">{planHint}</p> : null}
        <div className="grid gap-4 xl:grid-cols-3">
          <KpiCard
            label="Расход"
            value={formatMoneyByCurrencies(totals.spendByCurrency, includeVat)}
            secondaryValue={formatMoneyByCurrencies(planTotals.spendByCurrency, includeVat)}
            cornerValue={spendProgress !== null ? formatPercentPlain(spendProgress) : null}
            cornerTone={spendCornerTone}
            progressValue={spendProgress !== null ? String(spendProgress) : null}
            progressMarkerValue={spendPlanToDatePercent !== null ? String(spendPlanToDatePercent) : null}
            progressTone="warning"
            planToDateLabel={spendPlanLabel}
            planToDateValue={rangeProgress.hasStarted ? formatMoneyByCurrencies(spendPlanTodayByCurrency, includeVat) : '—'}
            footerTiles={[spendForecastTile, spendDeltaTile]}
          />
          <KpiCard
            label="Лиды"
            value={formatNumber(totals.conversions)}
            secondaryValue={formatNumber(planTotals.leads)}
            cornerValue={leadsProgress !== null ? formatPercentPlain(leadsProgress) : null}
            cornerTone={leadsCornerTone}
            progressValue={leadsProgress !== null ? String(leadsProgress) : null}
            progressMarkerValue={leadsPlanToDatePercent !== null ? String(leadsPlanToDatePercent) : null}
            progressTone={leadsDeltaTile.tone}
            planToDateLabel={leadsPlanLabel}
            planToDateValue={rangeProgress.hasStarted ? formatNumber(leadsPlanToday) : '—'}
            footerTiles={[leadsForecastTile, leadsDeltaTile]}
          />
          <KpiCard
            label="CPL"
            value={formatCpl(averageCpa, selectedCurrency, includeVat)}
            secondaryValue={formatCpl(planTotals.cpa, selectedCurrency, includeVat)}
            reserveProgressSpace={shouldReserveCplProgressSpace}
            footerTiles={[
              cplDeltaTile,
              {
                label: 'Отклонение',
                value: cplDeviationState.value,
                hint: cplDeviationState.hint,
                tone: cplDeviationState.tone
              }
            ]}
          />
        </div>
      </div>

      <Card className={SURFACE_CARD_CLASS}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">Дневная детализация</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                {summaryRows.length > 0 ? `Строк в таблице: ${formatNumber(summaryRows.length)}` : 'За выбранный период пока нет данных.'}
              </p>
            </div>
          </div>

          {summaryRows.length === 0 ? (
            <div className="text-sm text-muted-foreground">Нет данных по выбранным фильтрам.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-muted-foreground">
                    <th className="pb-2 font-medium">
                      <SortableHeader label="Таргетолог" activeSort={sort} ascValue="account_asc" descValue="account_desc" onChange={setSort} />
                    </th>
                    <th className="pb-2 font-medium">
                      <SortableHeader label="Канал" activeSort={sort} ascValue="service_asc" descValue="service_desc" onChange={setSort} />
                    </th>
                    <th className="pb-2 font-medium">
                      <SortableHeader label="Дата" activeSort={sort} ascValue="date_asc" descValue="date_desc" onChange={setSort} />
                    </th>
                    <th className="pb-2 font-medium text-right">
                      <SortableHeader label="Расход" activeSort={sort} ascValue="spend_asc" descValue="spend_desc" onChange={setSort} align="right" />
                    </th>
                    <th className="pb-2 font-medium text-right">
                      <SortableHeader label="Лиды" activeSort={sort} ascValue="conversions_asc" descValue="conversions_desc" onChange={setSort} align="right" />
                    </th>
                    <th className="pb-2 font-medium text-right">
                      <SortableHeader label="CPL" activeSort={sort} ascValue="cpl_asc" descValue="cpl_desc" onChange={setSort} align="right" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row) => (
                    <tr key={row.key} className="border-b border-border/60">
                      <td className="py-3 font-medium">{row.accountLabel}</td>
                      <td className="py-3">{row.serviceLabel}</td>
                      <td className="py-3">{formatFullDate(row.date)}</td>
                      <td className="py-3 text-right">{formatMoneyByCurrencies(row.spendByCurrency, includeVat)}</td>
                      <td className="py-3 text-right">{formatNumber(row.conversions)}</td>
                      <td className="py-3 text-right">{formatCpl(row.cpl, getSingleCurrency(row.spendByCurrency), includeVat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
